import classnames from 'classnames'
import moment from 'moment-timezone'
import { useState } from 'react'
import { DownloadOutlined } from '@ant-design/icons'
import { message } from 'antd'
import { getUniqueWorkOrderAttachmentsData } from '@OpsQuality/logic'
import { SHORT_DATE_TIME_FORMAT } from '@OpsQuality/constants'

import Box from '~/core/Box'
import { isBlockedFileName } from '~/lib/fileExtensions'
import { DOWNLOAD_BLOCKED_ATTACHMENT_ERROR } from '~/lib/flashMsgs'

import {
    getExtraProps,
    ZOOM_SCALE,
    isVisibleToCustomer,
} from './components/logic'
import attachmentStyles from './components/styles.css'
import { FileListType, SelectedFileStateType } from './components/types'
import PreviewModal from './components/PreviewModal'
import { CustomerVisibility } from './components/CustomerVisibility'
import { AttachmentWithCategoryType } from './types'

export default function FileList(props: FileListType) {
    const [selectedFileState, setSelectedFile] =
        useState<SelectedFileStateType>({
            selectedFile: null,
            imgIndex: -1,
            isImage: false,
            rotation: 0,
            scale: 1,
            offsetX: 0,
            offsetY: 0,
        })

    const {
        files,
        modalControls,
        modalCss,
        showNames,
        typeOfModule,
        showIcons,
        large,
        onRemove,
        attachmentTitle,
        hideDateTime,
        small,
        onSetVisibleToCustomer,
        showVisibilityIcon,
        carStyle,
    } = props

    const { selectedFile, imgIndex, scale } = selectedFileState
    const smallNotLarge = small && !large
    const largeAndCarStyle = large && carStyle

    const uniqueWorkOrderAttachmentData =
        getUniqueWorkOrderAttachmentsData(files)
    const extraProps = getExtraProps()
    const functionalProp = extraProps.functionalProp
    const canAttachmentBeDeleted =
        modalControls.canBeDeleted &&
        selectedFile &&
        functionalProp(selectedFile).canBeDeletedProp
    const onPreview = (fileData: AttachmentWithCategoryType) => {
        const index = uniqueWorkOrderAttachmentData.indexOf(fileData)
        const isImg = Boolean(
            fileData?.attachment?.mimeType?.startsWith('image'),
        )
        const fileName = fileData?.attachment?.fileName || ''

        if (isBlockedFileName(fileName)) {
            message.error(DOWNLOAD_BLOCKED_ATTACHMENT_ERROR)
            return
        }

        setSelectedFile({
            ...selectedFileState,
            selectedFile: fileData,
            imgIndex: index,
            isImage: isImg,
        })
    }
    const closeFileListModal = () => {
        setSelectedFile({
            selectedFile: null,
            rotation: 0,
            scale: 1,
            imgIndex: -1,
            isImage: false,
            offsetX: 0,
            offsetY: 0,
        })
    }
    const rotateImg = (counterClockwise?: boolean) => {
        const ROTATE_90_DEGREES = 90

        let newRotation = selectedFileState.rotation

        if (counterClockwise) {
            newRotation += ROTATE_90_DEGREES
        } else {
            newRotation -= ROTATE_90_DEGREES
        }

        setSelectedFile({
            ...selectedFileState,
            rotation: newRotation,
        })
    }
    const onPan = (dx: number, dy: number) => {
        setSelectedFile({
            ...selectedFileState,
            offsetX: dx,
            offsetY: dy,
        })
    }
    const toggleAttachment = (nextImg?: boolean) => {
        const index = nextImg ? imgIndex + 1 : imgIndex - 1
        const isImg = Boolean(
            uniqueWorkOrderAttachmentData[
                index
            ]?.attachment?.mimeType?.startsWith('image'),
        )

        setSelectedFile({
            imgIndex: index,
            selectedFile: uniqueWorkOrderAttachmentData[index],
            scale: 1,
            rotation: 0,
            isImage: isImg,
            offsetX: 0,
            offsetY: 0,
        })
    }
    const openInNew = (downloadUrl: string) =>
        window.open(downloadUrl, '_blank', 'noopener')
    const zoomIn = () => {
        setSelectedFile({
            ...selectedFileState,
            scale: scale + ZOOM_SCALE,
        })
    }
    const zoomOut = () => {
        setSelectedFile({
            ...selectedFileState,
            scale: scale - ZOOM_SCALE,
        })
    }
    const leftButtonDisabled = () => imgIndex === 0
    const rightButtonDisabled = () =>
        imgIndex === uniqueWorkOrderAttachmentData.length - 1

    return (
        <>
            {uniqueWorkOrderAttachmentData.map(
                (file: AttachmentWithCategoryType) => {
                    const { icon, style } = functionalProp(file)
                    const { id, visibleToCustomer } = file
                    const isVisibilityOn =
                        isVisibleToCustomer(visibleToCustomer)

                    return (
                        <div
                            key={file?.id}
                            className={`${
                                smallNotLarge
                                    ? attachmentStyles.fileListImgWrapSmall
                                    : attachmentStyles.fileListImgWrap
                            }
                            ${
                                largeAndCarStyle
                                    ? attachmentStyles.carfileListImgWrapLarge
                                    : ''
                            } ${
                                large && !carStyle
                                    ? attachmentStyles.fileListImgWrapLarge
                                    : ''
                            } ${
                                showNames
                                    ? attachmentStyles.imageWrapflexStyle
                                    : ''
                            }`}
                        >
                            <Box>
                                {showNames && (
                                    <Box className={attachmentStyles.fileName}>
                                        {file?.attachment?.fileName}
                                    </Box>
                                )}
                                <Box
                                    key={file?.id}
                                    className={
                                        smallNotLarge
                                            ? attachmentStyles.fileListImgWrapWrapSmall
                                            : attachmentStyles.fileListImgWrapWrap
                                    }
                                >
                                    {showVisibilityIcon && (
                                        <CustomerVisibility
                                            visibleToCustomer={
                                                visibleToCustomer
                                            }
                                            id={id}
                                            onSetVisibleToCustomer={
                                                onSetVisibleToCustomer
                                            }
                                            typeOfModule={typeOfModule}
                                        />
                                    )}
                                    {showIcons && (
                                        <Box
                                            className={classnames(
                                                attachmentStyles.uploadedBy,
                                                style,
                                            )}
                                        >
                                            {icon}
                                        </Box>
                                    )}
                                    {file?.attachment?.mimeType?.startsWith(
                                        'image',
                                    ) ? (
                                        <img
                                            className={`${
                                                largeAndCarStyle
                                                    ? attachmentStyles.carFileListImg
                                                    : attachmentStyles.fileListImg
                                            } ${
                                                showNames
                                                    ? attachmentStyles.fileListImgWithTitle
                                                    : ''
                                            } ${
                                                small
                                                    ? attachmentStyles.fileListImgSmall
                                                    : ''
                                            } ${
                                                !isVisibilityOn
                                                    ? attachmentStyles.visibilityOffImagestyle
                                                    : ''
                                            }`}
                                            src={
                                                file.attachment?.downloadUrl ||
                                                ''
                                            }
                                            onClick={() => {
                                                onPreview(file)
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            className={`
                                        ${attachmentStyles.fileListOther}
                                        ${
                                            !isVisibilityOn
                                                ? attachmentStyles.visibilityOffImagestyle
                                                : ''
                                        }
                                    `}
                                        >
                                            <a
                                                onClick={() => {
                                                    onPreview(file)
                                                }}
                                            >
                                                <DownloadOutlined
                                                    style={{
                                                        fontSize: '34px',
                                                        color: '#08c',
                                                    }}
                                                />
                                                {file?.attachment?.extension}
                                            </a>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            {!hideDateTime && (
                                <div
                                    className={`${attachmentStyles.dateCaption}`}
                                    style={{
                                        marginTop: showNames ? '4px' : '0px',
                                    }}
                                >
                                    {moment(file.createdAt).format(
                                        SHORT_DATE_TIME_FORMAT,
                                    )}
                                </div>
                            )}
                        </div>
                    )
                },
            )}
            <PreviewModal
                modalCss={modalCss}
                onRemove={onRemove}
                canAttachmentBeDeleted={canAttachmentBeDeleted}
                selectedFileState={selectedFileState}
                modalControls={modalControls}
                closeFileListModal={closeFileListModal}
                rotateImg={rotateImg}
                toggleAttachment={toggleAttachment}
                openInNew={openInNew}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                onPan={onPan}
                leftButtonDisabled={leftButtonDisabled}
                rightButtonDisabled={rightButtonDisabled}
                typeOfModule={typeOfModule}
                attachmentTitle={attachmentTitle}
            />
        </>
    )
}
